// src/components/AccountInfo/index.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AccountInfo.css';

import DealershipModal from '../DealershipModal';
import EmployeeModal from '../EmployeeModal';
import ChooseDealershipModal from '../ChooseDealershipModal';
import UserProfileModal from '../UserProfileModal';

import { useDealership } from '../../contexts/DealershipContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import UserContext from '../../contexts/UserContext';

const formatPhoneNumber = (phone = '') => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10
    ? `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    : phone;
};

const AccountInfo = () => {
  const {
    dealership,
    loading: dealLoading,
    fetchDealership,
    dealerships
  } = useDealership();

  const {
    employees,
    fetchEmployees,
    loading: empLoading
  } = useEmployees();

  const { user } = useContext(UserContext);

  const [isDealershipModalOpen, setIsDealershipModalOpen] = useState(false);
  const [modalDealership, setModalDealership] = useState(null);
  const [isChooseDealershipModalOpen, setIsChooseDealershipModalOpen] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);

  // prevent refetch loops
  const employeesFetchedRef = useRef({});

  useEffect(() => {
    if (dealership?.bac && !employeesFetchedRef.current[dealership.bac]) {
      employeesFetchedRef.current[dealership.bac] = true;
      fetchEmployees(dealership.bac);
    }
  }, [dealership?.bac, fetchEmployees]);

  // A reload of a dealership already on screen runs behind the page. Showing
  // the loading card instead would also unmount any open form mid-save.
  const loading = (dealLoading && !dealership) || empLoading;
  // While that reload runs, the dealership buttons wait for it. An edit
  // started now could move the BAC while the reload is still asking for the
  // old one; the server refuses that request and the page would drop the
  // dealership and empty the cart. After Switch, the dealership on screen is
  // also only the list entry until its details arrive.
  const dealershipBusy = Boolean(dealLoading);

  if (loading) {
    return (
      <div className="account-info-container">
        <div className="account-card loading-card">Loading your account…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="orders-page">
        <div className="orders-header">
          <h1>My Account</h1>
        </div>
        <div className="no-dealership">
          <p>Please log in to see this.</p>
        </div>
      </div>
    );
  }

  const hasMultipleDealerships = dealerships?.length > 1;
  const hasShippingAddress = Boolean(
    dealership?.shippingAddress1 ||
    dealership?.shippingAddress2 ||
    dealership?.shippingCity ||
    dealership?.shippingState ||
    dealership?.shippingPostalCode ||
    dealership?.shippingCountry
  );

  const openEditDealership = () => {
    setModalDealership(dealership);
    setIsDealershipModalOpen(true);
  };
  const openAddDealership = () => {
    setModalDealership(null);
    setIsDealershipModalOpen(true);
  };
  // The modal has already saved by the time this runs. Saving again here sent
  // the modal's result object as the dealership, and after a BAC change it
  // went to the old BAC, which the dealer is no longer linked to.
  const handleDealershipSubmit = async () => {
    setIsDealershipModalOpen(false);
  };
  const handleDealershipCreated = async () => {
    setIsDealershipModalOpen(false);
    await fetchDealership();
  };
  const addEmployee = () => { setSelectedEmployee(null); setIsEmployeeModalOpen(true); };
  const editEmployee = (emp) => { setSelectedEmployee(emp); setIsEmployeeModalOpen(true); };
  const closeEmployeeModal = () => { setIsEmployeeModalOpen(false); setSelectedEmployee(null); };

  return (
    <div className="account-info-container">
      <h1 className="account-page-title">My Account</h1>

      {!dealership ? (
        <section className="account-card no-dealership-card">
          <p>You haven't created a dealership yet.</p>
          <button className="btn btn-primary" onClick={openAddDealership}>
            Create New Dealership
          </button>
        </section>
      ) : (
        <>
          {/* Dealership */}
          <section className="account-card">
            <div className="card-head">
              <h2 className="card-title">Dealership</h2>
              <div className="card-actions">
                {hasMultipleDealerships && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsChooseDealershipModalOpen(true)}
                    disabled={dealershipBusy}
                  >
                    Switch
                  </button>
                )}
                <button className="btn btn-secondary" onClick={openAddDealership} disabled={dealershipBusy}>
                  Add New
                </button>
                <button className="btn btn-primary" onClick={openEditDealership} disabled={dealershipBusy}>
                  Edit
                </button>
              </div>
            </div>

            <h3 className="dealership-name">{dealership.name}</h3>

            <dl className="detail-grid">
              <div className="detail">
                <dt>Address</dt>
                <dd>
                  <div>{dealership.streetAddress}</div>
                  {dealership.addressLine2 && <div>{dealership.addressLine2}</div>}
                  <div>{dealership.city}, {dealership.state} {dealership.zip}</div>
                </dd>
              </div>
              <div className="detail">
                <dt>Sales Phone</dt>
                <dd>{formatPhoneNumber(dealership.salesPhone)}</dd>
              </div>
              <div className="detail">
                <dt>Website</dt>
                <dd>
                  {dealership.url ? (
                    <a href={dealership.url} target="_blank" rel="noopener noreferrer">
                      {dealership.url.replace(/^https?:\/\//, '')}
                    </a>
                  ) : '—'}
                </dd>
              </div>
            </dl>
          </section>

          {/* Shipping */}
          <section className="account-card">
            <div className="card-head">
              <h2 className="card-title">Shipping Address</h2>
              <button className="btn btn-secondary" onClick={openEditDealership} disabled={dealershipBusy}>
                Edit
              </button>
            </div>
            {dealership.shippingSameAsDealership ? (
              <p className="muted">Same as dealership address.</p>
            ) : hasShippingAddress ? (
              <address className="shipping-address">
                <div>
                  {dealership.shippingAddress1}
                  {dealership.shippingAddress2 ? `, ${dealership.shippingAddress2}` : ''}
                </div>
                <div>
                  {[dealership.shippingCity, dealership.shippingState, dealership.shippingPostalCode, dealership.shippingCountry]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </address>
            ) : (
              <p className="muted">No shipping address on file.</p>
            )}
          </section>

          {/* Employees */}
          <section className="account-card">
            <div className="card-head">
              <h2 className="card-title">Employees</h2>
              <button className="btn btn-primary" onClick={addEmployee}>
                Add Employee
              </button>
            </div>
            <p className="card-hint">Employees added here can be featured in your ads.</p>
            {employees.length ? (
              <ul className="employee-list">
                {employees.map(emp => (
                  <li key={emp.id} className="employee-row">
                    <div className="employee-info">
                      <span className="employee-name">{emp.name}</span>
                      <span className="employee-meta">
                        {[emp.title, emp.email].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => editEmployee(emp)}
                    >
                      Edit
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No employees yet.</p>
            )}
          </section>
        </>
      )}

      {/* Profile */}
      <section className="account-card">
        <div className="card-head">
          <h2 className="card-title">Your Profile</h2>
          <button
            className="btn btn-secondary"
            onClick={() => setIsUserProfileModalOpen(true)}
          >
            Edit Profile
          </button>
        </div>
        <dl className="detail-grid">
          <div className="detail">
            <dt>Name</dt>
            <dd>{user.name || '—'}</dd>
          </div>
          <div className="detail">
            <dt>Email</dt>
            <dd>{user.email || '—'}</dd>
          </div>
        </dl>
        <Link className="text-link" to="/orders">View order history</Link>
      </section>

      {/* Modals */}
      {isDealershipModalOpen && (
        <DealershipModal
          dealership={modalDealership}
          onClose={() => setIsDealershipModalOpen(false)}
          onSubmit={modalDealership ? handleDealershipSubmit : handleDealershipCreated} />
      )}
      {isChooseDealershipModalOpen && (
        <ChooseDealershipModal onClose={() => setIsChooseDealershipModalOpen(false)} />
      )}
      {isEmployeeModalOpen && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={closeEmployeeModal}
          onEmployeeAdded={() => {
            employeesFetchedRef.current[dealership.bac] = false;
            fetchEmployees(dealership.bac);
            closeEmployeeModal();
          }}
        />
      )}
      {isUserProfileModalOpen && (
        <UserProfileModal onClose={() => setIsUserProfileModalOpen(false)} />
      )}
    </div>
  );
};

export default AccountInfo;
